/**
 * Upload Service
 * Cross-cutting file upload functions used by multiple domains
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export interface Thumbnails {
	sm: string;
	md: string;
	lg: string;
}

export interface UploadedFile {
	url: string;
	filename: string;
	thumbnails?: Thumbnails;
}

export interface UploadOptions {
	folder?: string;
	entityType?: "users" | "projects";
	entityId?: string;
}

export async function uploadFile(
	file: File,
	options?: UploadOptions,
): Promise<{
	success: boolean;
	url?: string;
	thumbnails?: Thumbnails;
	error?: string;
}> {
	try {
		const formData = new FormData();
		formData.append("file", file);
		const params: Record<string, string> = {};
		if (options?.folder) params.folder = options.folder;
		if (options?.entityType) params.entityType = options.entityType;
		if (options?.entityId) params.entityId = options.entityId;

		const response = await apiClient.post(`${API_BASE}/upload`, formData, {
			params: Object.keys(params).length > 0 ? params : undefined,
		});
		const data = response.data.data;
		return {
			success: true,
			url: data.url,
			thumbnails: data.thumbnails,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to upload file",
		};
	}
}

export async function uploadMultipleFiles(
	files: File[],
	options?: UploadOptions,
): Promise<{
	success: boolean;
	urls?: string[];
	results?: UploadedFile[];
	error?: string;
}> {
	try {
		const formData = new FormData();
		for (const file of files) {
			formData.append("files", file);
		}
		const params: Record<string, string> = {};
		if (options?.folder) params.folder = options.folder;
		if (options?.entityType) params.entityType = options.entityType;
		if (options?.entityId) params.entityId = options.entityId;

		const response = await apiClient.post(
			`${API_BASE}/upload/multiple`,
			formData,
			{
				params: Object.keys(params).length > 0 ? params : undefined,
			},
		);
		const data = response.data.data;
		const results: UploadedFile[] | undefined = data.results;
		const urls: string[] = results
			? results.map((r) => r.url)
			: (data.urls ?? []);
		return {
			success: true,
			urls,
			results,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to upload files",
		};
	}
}
